import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import EngineeringLoginRoute, { engineeringLoginState } from './pages/ai-engineering/EngineeringLoginRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SuperAdminProvider } from './pages/super-admin/SuperAdminContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import './App.css';

// Lazy-loaded pages — only downloaded when the route is visited
const Login = lazy(() => import('./pages/Login'));
const InvestorDeck = lazy(() => import('./pages/InvestorDeck'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const Sprints = lazy(() => import('./pages/Sprints'));
const SprintDetail = lazy(() => import('./pages/SprintDetail'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Reports = lazy(() => import('./pages/Reports'));
const Forecasts = lazy(() => import('./pages/Forecasts'));
const Ideas = lazy(() => import('./pages/Ideas'));
const Admin = lazy(() => import('./pages/Admin'));
const AIAnalysis = lazy(() => import('./pages/AIAnalysis'));
const Executive = lazy(() => import('./pages/Executive'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const AIProjectManager = lazy(() => import('./pages/AIProjectManager'));
const AutomationRules = lazy(() => import('./pages/AutomationRules'));
const WorkflowBuilder = lazy(() => import('./pages/WorkflowBuilder'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PortfolioOverview = lazy(() => import('./pages/PortfolioOverview'));
const GoalCategories = lazy(() => import('./pages/GoalCategories'));
const PortfolioTeams = lazy(() => import('./pages/PortfolioTeams'));
const PortfolioImport = lazy(() => import('./pages/PortfolioImport'));
const PortfolioRisks = lazy(() => import('./pages/PortfolioRisks'));
const PortfolioRoadmapPage = lazy(() => import('./pages/PortfolioRoadmapPage'));
const PortfolioReports = lazy(() => import('./pages/PortfolioReports'));
const PortfolioWeeklyBrief = lazy(() => import('./pages/PortfolioWeeklyBrief'));
const ResourcePlanning = lazy(() => import('./pages/ResourcePlanning'));
const Templates = lazy(() => import('./pages/Templates'));
const DocumentHub = lazy(() => import('./pages/DocumentHub'));
const OKRs = lazy(() => import('./pages/OKRs'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const KanbanBoard = lazy(() => import('./pages/KanbanBoard'));
const DeveloperDashboard = lazy(() => import('./pages/DeveloperDashboard'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const MyAgents = lazy(() => import('./pages/MyAgents'));
const BillingPlans = lazy(() => import('./pages/BillingPlans'));
const OrgSettings = lazy(() => import('./pages/OrgSettings'));
const JoinOrg = lazy(() => import('./pages/JoinOrg'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RiskDashboard = lazy(() => import('./pages/RiskDashboard'));
const GanttChart = lazy(() => import('./pages/GanttChart'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const CalendarView = lazy(() => import('./pages/CalendarView'));
const GuestProjectView = lazy(() => import('./pages/GuestProjectView'));
const CustomDashboard = lazy(() => import('./pages/CustomDashboard'));
const PublicFormView = lazy(() => import('./pages/PublicFormView'));
const WorkGraph = lazy(() => import('./pages/WorkGraph'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const ScenarioPlanning = lazy(() => import('./pages/ScenarioPlanning'));
const StakeholderPortals = lazy(() => import('./pages/StakeholderPortals'));
const PublicPortalView = lazy(() => import('./pages/PublicPortalView'));
const Integrations = lazy(() => import('./pages/Integrations'));
const SlackShortcuts = lazy(() => import('./pages/SlackShortcuts'));
const SlackUserMappings = lazy(() => import('./pages/SlackUserMappings'));
const ProactiveAI = lazy(() => import('./pages/ProactiveAI'));
const AISkills = lazy(() => import('./pages/AISkills'));
const AIAutomations = lazy(() => import('./pages/AIAutomations'));
const SharedAutomationView = lazy(() => import('./pages/SharedAutomationView'));
const CollabDocs = lazy(() => import('./pages/CollabDocs'));
const PersonaDashboard = lazy(() => import('./pages/PersonaDashboard'));
const ProgressTracker = lazy(() => import('./pages/ProgressTracker'));
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const TeamMemberProfile = lazy(() => import('./pages/TeamMemberProfile'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const Clients = lazy(() => import('./pages/Clients'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const OutcomesPage = lazy(() => import('./pages/OutcomesPage'));
const GuidedWorkflows = lazy(() => import('./pages/GuidedWorkflows'));
const CTODashboard = lazy(() => import('./pages/CTODashboard'));
const LiveActivity = lazy(() => import('./pages/LiveActivity'));
const BudgetBurn = lazy(() => import('./pages/BudgetBurn'));
const FinanceOverview = lazy(() => import('./pages/FinanceOverview'));
const Invoices = lazy(() => import('./pages/Invoices'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const QuickStartGuide = lazy(() => import('./pages/QuickStartGuide'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AIEngineering = lazy(() => import('./pages/ai-engineering/AIEngineering'));
const AIEngineeringTaskDetail = lazy(() => import('./pages/ai-engineering/TaskDetail'));
const GitHubOnboardingCallback = lazy(() => import('./pages/ai-engineering/components/GitHubOnboarding').then((module) => ({ default: module.GitHubOnboardingCallback })));
const AIEngineeringUxGallery = lazy(() => import('./pages/ai-engineering/UxGallery'));

// Super Admin
const SuperAdminLogin = lazy(() => import('./pages/super-admin/SuperAdminLogin'));
const SuperAdminLayout = lazy(() => import('./pages/super-admin/SuperAdminLayout'));
const SADashboard = lazy(() => import('./pages/super-admin/SADashboard'));
const SATenants = lazy(() => import('./pages/super-admin/SATenants'));
const SATenantDetail = lazy(() => import('./pages/super-admin/SATenantDetail'));
const SABilling = lazy(() => import('./pages/super-admin/SABilling'));
const SADemoReset = lazy(() => import('./pages/super-admin/SADemoReset'));
const SASystemPerformance = lazy(() => import('./pages/super-admin/SASystemPerformance'));
const SADataQuality = lazy(() => import('./pages/super-admin/SADataQuality'));
const SASecurityAudit = lazy(() => import('./pages/super-admin/SASecurityAudit'));
const SAMetricsDashboard = lazy(() => import('./pages/super-admin/SAMetricsDashboard'));
const SARoutePermissions = lazy(() => import('./pages/super-admin/SARoutePermissions'));
const SAReliability = lazy(() => import('./pages/super-admin/SAReliability'));
const SAAccessDenials = lazy(() => import('./pages/super-admin/SAAccessDenials'));
const SACreditManagement = lazy(() => import('./pages/super-admin/SACreditManagement'));
const SACreditAnalytics = lazy(() => import('./pages/super-admin/SACreditAnalytics'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const ProtectedRoute = ({ children, requiredRole, allowedRoles, blockClient }) => {
  const location = useLocation();
  const { isAuthenticated, loading, user, isClientUser } = useAuth();

  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" state={engineeringLoginState(location.pathname)} replace />;
  // Client users are blocked from most pages
  if (blockClient && isClientUser) {
    return <Navigate to="/projects" replace />;
  }
  // Array-based role check (allowedRoles takes precedence)
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user?.role) && user?.role !== 'admin') {
      logAccessDenial(window.location.pathname);
      return <Navigate to="/dashboard" replace />;
    }
  }
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    logAccessDenial(window.location.pathname);
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Fire-and-forget access denial logger
function logAccessDenial(attemptedPath) {
  const token = localStorage.getItem('token');
  if (!token) return;
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  fetch(`${API_URL}/api/v1/access-denials/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ attempted_path: attemptedPath, redirected_to: '/dashboard' }),
  }).catch(() => {});
}

const SmartDashboard = () => {
  const { user, isClientUser, isDeveloper } = useAuth();
  if (isDeveloper || isClientUser) return <DeveloperDashboard />;
  return <Dashboard />;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/deck" element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0F]" />}><InvestorDeck /></Suspense>} />
        <Route path="/login" element={<EngineeringLoginRoute authenticated={isAuthenticated}><Login /></EngineeringLoginRoute>} />
        <Route path="/join" element={<JoinOrg />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/shared/:token" element={<GuestProjectView />} />
        <Route path="/portal/:slug" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>}><PublicPortalView /></Suspense>} />
        <Route path="/form/:slug" element={<PublicFormView />} />
        {process.env.NODE_ENV !== 'production' && (
          <Route path="/ai-engineering/ux-gallery" element={<AIEngineeringUxGallery />} />
        )}
        {/* Root: guests see landing; signed-in users go to dashboard */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
        />

        {/* Authenticated app shell — pathless layout so /account etc. never render LandingPage */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<ErrorBoundary><SmartDashboard /></ErrorBoundary>} />
          <Route path="progress" element={<ErrorBoundary><ProgressTracker /></ErrorBoundary>} />
          <Route path="time" element={<ErrorBoundary><TimeTracking /></ErrorBoundary>} />
          <Route path="time-tracking" element={<ErrorBoundary><TimeTracking /></ErrorBoundary>} />
          <Route path="my-tasks" element={<ErrorBoundary><MyTasks /></ErrorBoundary>} />
          <Route path="my-agents" element={<ErrorBoundary><MyAgents /></ErrorBoundary>} />
          <Route path="agent-chat" element={<Navigate to="/my-agents?tab=chat" replace />} />
          <Route path="agent-chat/:sessionId" element={<Navigate to="/my-agents?tab=chat" replace />} />
          <Route path="projects" element={<ErrorBoundary><Projects /></ErrorBoundary>} />
          <Route path="projects/:projectId" element={<ErrorBoundary><ProjectDetail /></ErrorBoundary>} />
          <Route path="sprints" element={<ErrorBoundary><Sprints /></ErrorBoundary>} />
          <Route path="sprints/:sprintId" element={<ErrorBoundary><SprintDetail /></ErrorBoundary>} />
          <Route path="tasks" element={<ErrorBoundary><Tasks /></ErrorBoundary>} />
          <Route path="kanban" element={<ErrorBoundary><KanbanBoard /></ErrorBoundary>} />
          <Route path="reports" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
          <Route path="forecasts" element={<ErrorBoundary><Forecasts /></ErrorBoundary>} />
          <Route path="ideas" element={<ErrorBoundary><Ideas /></ErrorBoundary>} />
          <Route path="ai-analysis" element={<ErrorBoundary><AIAnalysis /></ErrorBoundary>} />
          <Route path="ai-pm" element={<ProtectedRoute requiredRole="manager"><ErrorBoundary><AIProjectManager /></ErrorBoundary></ProtectedRoute>} />
          <Route path="automation" element={<ErrorBoundary><AutomationRules /></ErrorBoundary>} />
          <Route path="workflow-builder" element={<ErrorBoundary><WorkflowBuilder /></ErrorBoundary>} />
          <Route path="work-graph" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><WorkGraph /></ErrorBoundary></ProtectedRoute>} />
          <Route path="analytics" element={<ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>} />
          <Route path="scenarios" element={<ErrorBoundary><ScenarioPlanning /></ErrorBoundary>} />
          <Route path="stakeholder-portals" element={<ErrorBoundary><StakeholderPortals /></ErrorBoundary>} />
          <Route path="integrations" element={<ErrorBoundary><Integrations /></ErrorBoundary>} />
          <Route path="slack-shortcuts" element={<ErrorBoundary><SlackShortcuts /></ErrorBoundary>} />
          <Route path="slack-user-mappings" element={<ProtectedRoute requiredRole="admin"><ErrorBoundary><SlackUserMappings /></ErrorBoundary></ProtectedRoute>} />
          <Route path="ai-insights" element={<ErrorBoundary><ProactiveAI /></ErrorBoundary>} />
          <Route path="ai-skills" element={<ErrorBoundary><AISkills /></ErrorBoundary>} />
          <Route path="ai-automations" element={<ErrorBoundary><AIAutomations /></ErrorBoundary>} />
          <Route path="ai-engineering" element={<ProtectedRoute allowedRoles={['admin','owner','manager','developer','member','project_lead']}><ErrorBoundary><AIEngineering /></ErrorBoundary></ProtectedRoute>} />
          <Route path="ai-engineering/tasks/:taskId" element={<ProtectedRoute allowedRoles={['admin','owner','manager','developer','member','project_lead']}><ErrorBoundary><AIEngineeringTaskDetail /></ErrorBoundary></ProtectedRoute>} />
          <Route path="ai-engineering/github/setup" element={<ProtectedRoute allowedRoles={['admin','owner']}><ErrorBoundary><GitHubOnboardingCallback stage="setup" /></ErrorBoundary></ProtectedRoute>} />
          <Route path="ai-engineering/github/callback" element={<ProtectedRoute allowedRoles={['admin','owner']}><ErrorBoundary><GitHubOnboardingCallback stage="callback" /></ErrorBoundary></ProtectedRoute>} />
          <Route path="ai-automations/shared/:shareToken" element={<ErrorBoundary><SharedAutomationView /></ErrorBoundary>} />
          <Route path="collab" element={<ErrorBoundary><CollabDocs /></ErrorBoundary>} />
          <Route path="persona-dashboard" element={<ErrorBoundary><PersonaDashboard /></ErrorBoundary>} />
          <Route path="portfolio" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioOverview /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/programs" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><Portfolio /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/goal-categories" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><GoalCategories /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/teams" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioTeams /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/import" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioImport /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/risks" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioRisks /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/roadmap" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioRoadmapPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/reports" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioReports /></ErrorBoundary></ProtectedRoute>} />
          <Route path="portfolio/weekly-brief" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><PortfolioWeeklyBrief /></ErrorBoundary></ProtectedRoute>} />
          <Route path="resource-planning" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><ResourcePlanning /></ErrorBoundary></ProtectedRoute>} />
          <Route path="team" element={<ErrorBoundary><TeamPage /></ErrorBoundary>} />
          <Route path="team/:userId" element={<ErrorBoundary><TeamMemberProfile /></ErrorBoundary>} />
          <Route path="clients" element={<ErrorBoundary><Clients /></ErrorBoundary>} />
          <Route path="clients/:clientId" element={<ErrorBoundary><ClientDetail /></ErrorBoundary>} />
          <Route path="templates" element={<ErrorBoundary><Templates /></ErrorBoundary>} />
          <Route path="docs" element={<ErrorBoundary><DocumentHub /></ErrorBoundary>} />
          <Route path="okrs" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><OKRs /></ErrorBoundary></ProtectedRoute>} />
          <Route path="executive" element={<ProtectedRoute allowedRoles={['admin','owner','manager']}><ErrorBoundary><Executive /></ErrorBoundary></ProtectedRoute>} />
          <Route path="roadmap" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><Roadmap /></ErrorBoundary></ProtectedRoute>} />
          <Route path="calendar" element={<ErrorBoundary><CalendarView /></ErrorBoundary>} />
          <Route path="my-dashboard" element={<ErrorBoundary><CustomDashboard /></ErrorBoundary>} />
          <Route path="risk" element={<ProtectedRoute allowedRoles={['admin','owner','manager']}><ErrorBoundary><RiskDashboard /></ErrorBoundary></ProtectedRoute>} />
          <Route path="outcomes" element={<ProtectedRoute allowedRoles={['admin','owner','manager','project_lead']}><ErrorBoundary><OutcomesPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="guided-workflows" element={<ErrorBoundary><GuidedWorkflows /></ErrorBoundary>} />
          {/* Observability moved to super admin portal at /super-admin/performance */}
          <Route path="live-activity" element={<ProtectedRoute allowedRoles={['admin','owner','manager']}><ErrorBoundary><LiveActivity /></ErrorBoundary></ProtectedRoute>} />
          <Route path="cto-dashboard" element={<ProtectedRoute allowedRoles={['admin','owner','manager']}><ErrorBoundary><CTODashboard /></ErrorBoundary></ProtectedRoute>} />
          <Route path="gantt" element={<ErrorBoundary><GanttChart /></ErrorBoundary>} />
          <Route path="audit" element={<ErrorBoundary><AuditLog /></ErrorBoundary>} />
          <Route path="billing" element={<ErrorBoundary><BillingPlans /></ErrorBoundary>} />
          <Route path="budget-burn" element={<ProtectedRoute allowedRoles={['admin','owner']}><ErrorBoundary><BudgetBurn /></ErrorBoundary></ProtectedRoute>} />
          <Route path="finance" element={<ProtectedRoute requiredRole="admin"><ErrorBoundary><FinanceOverview /></ErrorBoundary></ProtectedRoute>} />
          <Route path="invoices" element={<ProtectedRoute requiredRole="admin"><ErrorBoundary><Invoices /></ErrorBoundary></ProtectedRoute>} />
          <Route path="org-settings" element={<ErrorBoundary><OrgSettings /></ErrorBoundary>} />
          <Route path="account" element={<ErrorBoundary><AccountSettings /></ErrorBoundary>} />
          <Route path="help" element={<ErrorBoundary><HelpCenter /></ErrorBoundary>} />
          <Route path="quick-start" element={<ErrorBoundary><QuickStartGuide /></ErrorBoundary>} />
          <Route
            path="admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <ErrorBoundary><Admin /></ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Super Admin Portal */}
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
        <Route path="/super-admin" element={<SuperAdminLayout />}>
          <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
          <Route path="dashboard" element={<SADashboard />} />
          <Route path="tenants" element={<SATenants />} />
          <Route path="tenants/:tenantId" element={<SATenantDetail />} />
          <Route path="billing" element={<SABilling />} />
          <Route path="credits" element={<SACreditManagement />} />
          <Route path="credit-analytics" element={<SACreditAnalytics />} />
          <Route path="demo-reset" element={<SADemoReset />} />
          <Route path="performance" element={<SASystemPerformance />} />
          <Route path="data-quality" element={<SADataQuality />} />
          <Route path="security" element={<SASecurityAudit />} />
          <Route path="metrics" element={<SAMetricsDashboard />} />
          <Route path="route-permissions" element={<SARoutePermissions />} />
          <Route path="reliability" element={<SAReliability />} />
          <Route path="access-denials" element={<SAAccessDenials />} />
        </Route>
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/landing'} replace />}
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SuperAdminProvider>
          <Router>
            <ErrorBoundary>
              <AppRoutes />
              <Toaster />
            </ErrorBoundary>
          </Router>
        </SuperAdminProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
